import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  Storefront, Package, ListChecks, Sparkle, ShareNetwork, Article,
  Eye, Rocket, ArrowLeft, ArrowRight,
  MagicWand, ArrowClockwise, FloppyDisk,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { EmptyState } from "../components/EmptyState";
import { PromoSelect } from "../components/PromoSelect";
import { StatusDot } from "../components/StatusBadge";
import { StepIndicator } from "../components/wizard/StepIndicator";
import { SelectableCard } from "../components/wizard/SelectableCard";
import { ProductCard } from "../components/wizard/ProductCard";
import { SocialPreviewCard } from "../components/SocialPreviewCard";
import { ConfirmDialog } from "../components/campaign/ConfirmDialog";
import { useWizardData, fetchProducts } from "../usePromo";
import { MARKETPLACES, SOCIAL_PLATFORMS, PLATFORM_BY_KEY } from "../platforms";
import { intervalMs, fmtRelative, fmtDuration, fmtDateTime } from "../campaignUtils";

const PanelStat = ({ label, value }) => (
  <div className="leading-tight">
    <div className="promo-overline">{label}</div>
    <div className="mt-0.5 text-sm font-medium tabular-nums">{value}</div>
  </div>
);

// Reusable Feed display-style radio (Marketplace link vs Marketplace images). Used per
// platform in the review step. `testidPrefix` keeps each platform's options uniquely testable.
const DisplayStylePicker = ({ value, onChange, t, testidPrefix }) => (
  <div>
    <div className="promo-overline mb-2">{t("promo.wizard.publishing.styleLabel")}</div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {[["marketplace_link", "link"], ["marketplace_images", "images"]].map(([val, key]) => {
        const active = value === val;
        return (
          <button key={val} type="button" onClick={() => onChange(val)}
            aria-pressed={active} data-testid={`${testidPrefix}-${val}`}
            className={cn("rounded-lg border p-4 text-left transition-colors",
              active ? "border-brand bg-brand/5 ring-1 ring-brand" : "border-border hover:bg-accent")}>
            <div className="flex items-center gap-2">
              <span className={cn("flex h-4 w-4 items-center justify-center rounded-full border",
                active ? "border-brand" : "border-muted-foreground/40")}>
                {active && <span className="h-2 w-2 rounded-full bg-brand" />}
              </span>
              <span className="text-sm font-medium">{t(`promo.wizard.publishing.${key}.title`)}</span>
            </div>
            <p className="mt-1 pl-6 text-xs text-muted-foreground">{t(`promo.wizard.publishing.${key}.desc`)}</p>
          </button>
        );
      })}
    </div>
  </div>
);


// Instagram Feed presentation picker (single image vs multi-image carousel). BOTH publish via
// the Official Instagram Graph API — the generated caption is NEVER changed. Kept fully separate
// from the marketplace link/images picker so Instagram stays independent from Facebook.
const InstagramDisplayPicker = ({ value, onChange, t }) => (
  <div>
    <div className="promo-overline mb-2">{t("promo.wizard.publishing.igStyleLabel")}</div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {[["single_image", "singleImage"], ["multiple_images", "multipleImages"]].map(([val, key]) => {
        const active = value === val;
        return (
          <button key={val} type="button" onClick={() => onChange(val)}
            aria-pressed={active} data-testid={`ig-display-style-${val}`}
            className={cn("rounded-lg border p-4 text-left transition-colors",
              active ? "border-brand bg-brand/5 ring-1 ring-brand" : "border-border hover:bg-accent")}>
            <div className="flex items-center gap-2">
              <span className={cn("flex h-4 w-4 items-center justify-center rounded-full border",
                active ? "border-brand" : "border-muted-foreground/40")}>
                {active && <span className="h-2 w-2 rounded-full bg-brand" />}
              </span>
              <span className="text-sm font-medium">{t(`promo.wizard.publishing.${key}.title`)}</span>
            </div>
            <p className="mt-1 pl-6 text-xs text-muted-foreground">{t(`promo.wizard.publishing.${key}.desc`)}</p>
          </button>
        );
      })}
    </div>
  </div>
);

// Facebook GROUP publishing (Browser Publisher). Loads the profile's member groups live via the
// session, multi-select, and a DEDICATED interval controlling ONLY the delay between group posts
// (independent from the campaign's normal-post interval). Shown only when an FB profile session is
// connected. Reuses the same generated caption + product image(s) as the Feed post.
const FacebookGroupsSection = ({ fbAccountId, value, onChange, t }) => {
  const [state, setState] = useState({ loading: false, list: [], error: null, loaded: false });
  const opts = value || {};
  const selected = opts.selected_group_ids || [];
  const load = async () => {
    if (!fbAccountId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/social/fb-web/session/${fbAccountId}/groups`);
      setState({ loading: false, list: data.groups || [], error: null, loaded: true });
    } catch (e) {
      setState({ loading: false, list: [], error: apiError(e), loaded: true });
    }
  };
  const toggle = (id) =>
    onChange({ selected_group_ids: selected.includes(id) ? selected.filter((g) => g !== id) : [...selected, id] });
  const allIds = state.list.map((g) => g.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
  return (
    <div className="mt-4 rounded-lg border border-border p-4" data-testid="fb-groups-section">
      <label className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{t("promo.wizard.publishing.groupsLabel")}</div>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("promo.wizard.publishing.groupsNote")}</p>
        </div>
        <Switch checked={!!opts.enabled} onCheckedChange={(v) => onChange({ enabled: v })}
          data-testid="fb-groups-enable" />
      </label>
      {opts.enabled && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={load} disabled={state.loading || !fbAccountId}
              data-testid="fb-groups-load"
              className={cn("rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors",
                state.loading || !fbAccountId ? "opacity-50" : "hover:bg-accent")}>
              {state.loading ? t("promo.wizard.publishing.groupsLoading") : t("promo.wizard.publishing.groupsLoad")}
            </button>
            {state.list.length > 0 && (
              <button type="button" data-testid="fb-groups-select-all"
                className="text-xs font-medium text-brand underline underline-offset-2"
                onClick={() => onChange({ selected_group_ids: allSelected ? [] : allIds })}>
                {allSelected ? t("promo.wizard.publishing.groupsClearAll") : t("promo.wizard.publishing.groupsSelectAll")}
              </button>
            )}
          </div>
          {state.error && <p className="text-xs text-destructive" data-testid="fb-groups-error">{state.error}</p>}
          {state.loaded && !state.error && state.list.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="fb-groups-empty">
              {t("promo.wizard.publishing.groupsEmpty")}</p>
          )}
          {state.list.length > 0 && (
            <div className="max-h-56 overflow-auto rounded-md border border-border" data-testid="fb-groups-list">
              {state.list.map((g) => {
                const on = selected.includes(g.id);
                return (
                  <button key={g.id} type="button" onClick={() => toggle(g.id)}
                    data-testid={`fb-group-toggle-${g.id}`} aria-pressed={on}
                    className="flex w-full items-center gap-3 border-b border-border/60 px-3 py-2 text-left last:border-0 hover:bg-accent">
                    <span className={cn("flex h-4 w-4 items-center justify-center rounded border",
                      on ? "border-brand bg-brand text-white" : "border-muted-foreground/40")}>
                      {on && <span className="text-[10px] leading-none">✓</span>}
                    </span>
                    <span className="truncate text-sm">{g.name}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("promo.wizard.publishing.groupsIntervalValue")}</Label>
              <Input type="number" min={1} value={opts.interval_value ?? 10}
                onChange={(e) => onChange({ interval_value: Number(e.target.value) || 1 })}
                className="bg-card" data-testid="fb-groups-interval-value" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("promo.wizard.publishing.groupsIntervalUnit")}</Label>
              <PromoSelect value={opts.interval_unit || "minutes"} testId="fb-groups-interval-unit"
                onChange={(v) => onChange({ interval_unit: v })}
                options={["minutes", "hours"].map((u) => ({ value: u, label: t(`promo.wizard.interval.units.${u}`) }))} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="fb-groups-selected-count">
            {t("promo.wizard.publishing.groupsSelected", { count: selected.length })}</p>
        </div>
      )}
    </div>
  );
};


const STRATEGY_META = {
  sequential: { Icon: ListChecks }, newest: { Icon: Sparkle }, random: { Icon: ArrowClockwise },
  best_sellers: { Icon: Package }, ai_decide: { Icon: MagicWand, ai: true },
};
const MODE_META = {
  original: { Icon: Article }, ai_enhance: { Icon: MagicWand, ai: true }, ai_complete: { Icon: Sparkle, ai: true },
};
const LANGUAGES = [
  { value: "en", label: "English" }, { value: "it", label: "Italiano" }, { value: "es", label: "Español" },
  { value: "fr", label: "Français" }, { value: "de", label: "Deutsch" }, { value: "pt", label: "Português" },
];
const UNITS = ["minutes", "hours", "days", "weeks", "months", "years"];

const INITIAL = {
  name: "", connection_id: null, source_platform: null, max_items: 5,
  selection_mode: "automatic", selected_product_ids: [],
  selection_strategy: "sequential", generation_mode: "original",
  account_ids: [], platforms: [],
  interval_value: 1, interval_unit: "hours", start_at: "", timezone: "UTC",
  language: "en", kb_id: null, prompt_template: null, provider: null,
  auto_create_campaign: true,
  platform_options: {
    facebook: { display_style: "marketplace_images", publish_to_story: false },
    linkedin: { display_style: "marketplace_images" },
    instagram: { display_style: "single_image", publish_to_story: false },
    facebook_groups: { enabled: false, selected_group_ids: [], interval_value: 10, interval_unit: "minutes" },
  },
};

function buildPayload(f) {
  const ai = {};
  // Original mode uses NO AI: never send provider/prompt/kb (backend ignores them
  // anyway). Values are kept in form state so they are restored if the user later
  // switches to an AI mode.
  if (f.generation_mode !== "original") {
    if (f.provider) ai.provider = f.provider;
    if (f.prompt_template) ai.prompt_template = f.prompt_template;
    if (f.kb_id) ai.kb_id = f.kb_id;
  }
  return {
    name: f.name?.trim() || "Nuova campagna",
    connection_id: f.connection_id, source_platform: f.source_platform,
    product_filter: {},
    selection_mode: f.selection_mode,
    selected_product_ids: f.selection_mode === "manual" ? f.selected_product_ids : [],
    selection_strategy: f.selection_strategy, selection_params: {},
    generation_mode: f.generation_mode, language: f.language, ai,
    platforms: f.platforms, account_ids: f.account_ids,
    interval_value: Number(f.interval_value) || 1, interval_unit: f.interval_unit,
    start_at: f.start_at || null, timezone: f.timezone, max_items: Number(f.max_items) || 1,
    auto_create_campaign: f.auto_create_campaign, preview_platforms: f.platforms,
    // PLATFORM-SPECIFIC presentation. Facebook (link|images + Story on Profile) and LinkedIn
    // (link=ARTICLE with preview image | images). Instagram has no options (images + caption).
    // Backward-compat: also send the legacy top-level fields mirroring the Facebook choice.
    platform_options: {
      facebook: {
        display_style: f.platform_options?.facebook?.display_style || "marketplace_images",
        publish_to_story: !!f.platform_options?.facebook?.publish_to_story,
      },
      linkedin: {
        display_style: f.platform_options?.linkedin?.display_style || "marketplace_images",
      },
      instagram: {
        display_style: f.platform_options?.instagram?.display_style || "single_image",
        publish_to_story: !!f.platform_options?.instagram?.publish_to_story,
      },
      // Facebook GROUP publishing (independent scheduling). Only sent when enabled + groups picked.
      facebook_groups: {
        enabled: !!f.platform_options?.facebook_groups?.enabled,
        selected_group_ids: f.platform_options?.facebook_groups?.selected_group_ids || [],
        interval_value: Number(f.platform_options?.facebook_groups?.interval_value) || 10,
        interval_unit: f.platform_options?.facebook_groups?.interval_unit || "minutes",
      },
    },
    display_style: f.platform_options?.facebook?.display_style || "marketplace_images",
    publish_to_story: !!f.platform_options?.facebook?.publish_to_story,
  };
}

function fromClone(p) {
  if (!p) return null;
  return {
    ...INITIAL,
    name: p.name ? `${p.name} (copia)` : "",
    connection_id: p.connection_id, source_platform: p.source_platform,
    max_items: p.max_items ?? 5,
    selection_mode: p.selection_mode || "automatic",
    selected_product_ids: p.selected_product_ids || [],
    selection_strategy: p.selection_strategy || "sequential",
    generation_mode: p.generation_mode || "original",
    account_ids: p.account_ids || [], platforms: p.platforms || [],
    interval_value: p.interval_value ?? 1, interval_unit: p.interval_unit || "hours",
    timezone: p.timezone || "UTC", language: p.language || "en",
    kb_id: p.ai?.kb_id || null, prompt_template: p.ai?.prompt_template || null, provider: p.ai?.provider || null,
    auto_create_campaign: p.auto_create_campaign ?? true,
    platform_options: {
      facebook: {
        display_style: p.platform_options?.facebook?.display_style || p.display_style || "marketplace_images",
        publish_to_story: p.platform_options?.facebook?.publish_to_story ?? p.publish_to_story ?? false,
      },
      linkedin: {
        display_style: p.platform_options?.linkedin?.display_style || p.display_style || "marketplace_images",
      },
      instagram: {
        display_style: p.platform_options?.instagram?.display_style || "single_image",
        publish_to_story: p.platform_options?.instagram?.publish_to_story ?? false,
      },
      facebook_groups: {
        enabled: p.platform_options?.facebook_groups?.enabled ?? false,
        selected_group_ids: p.platform_options?.facebook_groups?.selected_group_ids || [],
        interval_value: p.platform_options?.facebook_groups?.interval_value ?? 10,
        interval_unit: p.platform_options?.facebook_groups?.interval_unit || "minutes",
      },
    },
  };
}

export default function NewCampaign() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const wd = useWizardData();
  const [form, setForm] = useState(() => fromClone(location.state?.clone) || INITIAL);
  const [step, setStep] = useState(0);
  const [products, setProducts] = useState({ list: [], loading: false });
  const [preview, setPreview] = useState({ result: null, loading: false });
  const [submitting, setSubmitting] = useState(false);
  const [confirmMax, setConfirmMax] = useState(null); // { value } when reducing max below selected

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Update ONE platform's publishing options (capability-driven, platform-specific).
  const setPO = (platform, patch) => setForm((f) => ({
    ...f,
    platform_options: {
      ...f.platform_options,
      [platform]: { ...(f.platform_options?.[platform] || {}), ...patch },
    },
  }));

  // Manual selection: toggle a product in/out of the candidate pool (capped by max_items).
  const toggleProduct = (extId) => {
    const id = String(extId);
    setForm((f) => {
      const has = f.selected_product_ids.includes(id);
      if (has) return { ...f, selected_product_ids: f.selected_product_ids.filter((x) => x !== id) };
      if (f.selected_product_ids.length >= Number(f.max_items || 0)) return f; // at limit → ignore
      return { ...f, selected_product_ids: [...f.selected_product_ids, id] };
    });
  };

  // Max products change. In manual mode, reducing below the current selection asks first.
  const onMaxChange = (value) => {
    const n = Number(value);
    if (form.selection_mode === "manual" && n >= 1 && form.selected_product_ids.length > n) {
      setConfirmMax({ value });
      return;
    }
    set({ max_items: value });
  };
  const confirmReduceMax = () => {
    const n = Number(confirmMax.value);
    set({ max_items: confirmMax.value, selected_product_ids: form.selected_product_ids.slice(0, n) });
    setConfirmMax(null);
  };

  // Load catalog when the source changes. When the PRODUCTS step opens, the marketplace is synced
  // first (fix 02/11/2026): listings added/deleted on Etsy used to appear only after a manual
  // "Sincronizza" in E-commerce. If the sync fails, the last synced catalog is still shown.
  const onProductStep = step === 2;
  const [syncState, setSyncState] = useState({ syncing: false, at: null });
  useEffect(() => {
    if (!form.source_platform) return;
    let alive = true;
    setProducts((p) => ({ list: p.list, loading: true }));
    (async () => {
      if (onProductStep && form.connection_id) {
        setSyncState((s) => ({ ...s, syncing: true }));
        let at = null;
        try {
          await api.post(`/integrations/connections/${form.connection_id}/sync`);
          at = new Date().toISOString();
        } catch { /* keep the cached catalog */ }
        if (alive) setSyncState((s) => ({ syncing: false, at: at || s.at }));
      }
      const list = await fetchProducts(form.source_platform);
      if (alive) setProducts({ list, loading: false });
    })();
    return () => { alive = false; };
  }, [form.source_platform, form.connection_id, onProductStep]);

  const toggleAccount = (acc) => {
    setForm((f) => {
      const has = f.account_ids.includes(acc.id);
      const account_ids = has ? f.account_ids.filter((x) => x !== acc.id) : [...f.account_ids, acc.id];
      const platforms = [...new Set(
        wd.accounts.filter((a) => account_ids.includes(a.id)).map((a) => a.platform),
      )].filter((p) => ["facebook", "instagram", "linkedin", "x"].includes(p));
      return { ...f, account_ids, platforms };
    });
  };

  const runPreview = async () => {
    setPreview({ result: null, loading: true });
    try {
      const { data } = await api.post("/promotions/preview", buildPayload(form));
      setPreview({ result: data, loading: false });
    } catch (e) {
      setPreview({ result: null, loading: false });
      toast.error(apiError(e));
    }
  };

  // Fix 02/11/2026: never create the campaign twice. The created id is kept: if activation fails
  // (e.g. a timeout), pressing the button again only retries the activation of THAT campaign.
  const createdIdRef = useRef(null);
  const busyRef = useRef(false);
  const submit = async (mode) => {
    if (busyRef.current) return;          // ignore double clicks while a request is running
    busyRef.current = true;
    setSubmitting(true);
    try {
      if (!createdIdRef.current) {
        const { data: promo } = await api.post("/promotions", buildPayload(form));
        createdIdRef.current = promo.id;
      }
      if (mode === "activate") await api.post(`/promotions/${createdIdRef.current}/activate`);
      toast.success(t(`promo.wizard.toast.${mode}`));
      navigate("/promotions");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  };

  const accountsByPlatform = useMemo(() => {
    const map = {};
    wd.accounts.forEach((a) => { (map[a.platform] ||= []).push(a); });
    return map;
  }, [wd.accounts]);

  // ── Step definitions ────────────────────────────────────────────────────
  const steps = [
    { key: "source", label: t("promo.wizard.steps.source"), valid: !!form.connection_id && !!form.name.trim() },
    { key: "selectionMode", label: t("promo.wizard.steps.selectionMode"), valid: !!form.selection_mode },
    { key: "product", label: t("promo.wizard.steps.product"),
      valid: Number(form.max_items) >= 1 && (form.selection_mode !== "manual" || form.selected_product_ids.length >= 1) },
    { key: "strategy", label: t("promo.wizard.steps.strategy"), valid: !!form.selection_strategy },
    { key: "mode", label: t("promo.wizard.steps.mode"), valid: !!form.generation_mode },
    { key: "platforms", label: t("promo.wizard.steps.platforms"), valid: form.account_ids.length >= 1 },
    { key: "interval", label: t("promo.wizard.steps.interval"), valid: Number(form.interval_value) >= 1 },
    { key: "language", label: t("promo.wizard.steps.language"), valid: !!form.language },
    { key: "kb", label: t("promo.wizard.steps.kb"), valid: true },
    { key: "prompt", label: t("promo.wizard.steps.prompt"), valid: true },
    { key: "provider", label: t("promo.wizard.steps.provider"), valid: true },
    { key: "preview", label: t("promo.wizard.steps.preview"), valid: true },
    { key: "review", label: t("promo.wizard.steps.review"), valid: true },
    { key: "start", label: t("promo.wizard.steps.start"), valid: true },
  ];
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const goNext = () => {
    if (!current.valid) return;
    if (steps[step + 1]?.key === "preview") runPreview();
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  return (
    <div className="space-y-8" data-testid="new-campaign-wizard">
      <PromoPageHeader overline={t("promo.nav.new")} title={t("promo.wizard.title")} description={t("promo.wizard.subtitle")} />

      <div className="mx-auto max-w-3xl space-y-8">
        <StepIndicator steps={steps} current={step} onJump={setStep} />

        <div className="min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
            >
              <StepContent
                stepKey={current.key} form={form} set={set} setPO={setPO} wd={wd} t={t}
                products={products} syncState={syncState} preview={preview} runPreview={runPreview}
                accountsByPlatform={accountsByPlatform} toggleAccount={toggleAccount}
                toggleProduct={toggleProduct} onMaxChange={onMaxChange}
                submit={submit} submitting={submitting}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <ConfirmDialog
          open={!!confirmMax}
          title={t("promo.wizard.confirmMax.title")}
          description={t("promo.wizard.confirmMax.desc")}
          confirmLabel={t("promo.wizard.confirmMax.confirm")}
          cancelLabel={t("promo.wizard.confirmMax.cancel")}
          onConfirm={confirmReduceMax}
          onCancel={() => setConfirmMax(null)}
          danger
        />

        {/* Footer nav */}
        <div className="flex items-center justify-between border-t border-border pt-5">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
            data-testid="wizard-back"
          >
            <ArrowLeft size={15} /> {t("common.back")}
          </button>
          {!isLast && (
            <button
              onClick={goNext}
              disabled={!current.valid}
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
              data-testid="wizard-next"
            >
              {t("promo.wizard.next")} <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step content renderer ───────────────────────────────────────────────────
function StepContent(props) {
  const { stepKey, form, set, setPO, wd, t } = props;

  const helper = (txt) => <p className="text-sm text-muted-foreground">{txt}</p>;
  const heading = (title, sub) => (
    <div className="space-y-1.5">
      <h2 className="text-2xl font-medium tracking-tight">{title}</h2>
      {sub && helper(sub)}
    </div>
  );

  switch (stepKey) {
    case "source": {
      const options = wd.sources.map((s) => ({ ...s, m: MARKETPLACES.find((m) => m.key === s.connector_id) }));
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.source.title"), t("promo.wizard.source.help"))}
          <div className="space-y-2">
            <Label>{t("promo.wizard.source.name")}</Label>
            <Input
              value={form.name} onChange={(e) => set({ name: e.target.value })}
              placeholder={t("promo.wizard.source.namePh")} className="bg-card" data-testid="wizard-name"
            />
          </div>
          <div className="space-y-2">
            <Label>{t("promo.wizard.source.pick")}</Label>
            {options.length === 0 ? (
              <EmptyState Icon={Storefront} title={t("promo.wizard.source.empty")}
                description={t("promo.wizard.source.emptyDesc")} />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {options.map((s) => (
                  <SelectableCard
                    key={s.id} testId={`source-${s.id}`}
                    selected={form.connection_id === s.id}
                    onClick={() => set({ connection_id: s.id, source_platform: s.connector_id })}
                    Icon={s.m?.Icon || Storefront}
                    title={s.name} description={s.m?.label || s.connector_id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    case "selectionMode": {
      const MODES = [
        { key: "automatic", Icon: MagicWand },
        { key: "manual", Icon: ListChecks },
        { key: "mixed", Icon: Sparkle, disabled: true },
      ];
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.selectionMode.title"), t("promo.wizard.selectionMode.help"))}
          <div className="grid grid-cols-1 gap-3">
            {MODES.map((m) => (
              <SelectableCard
                key={m.key} testId={`selection-mode-${m.key}`}
                selected={!m.disabled && form.selection_mode === m.key}
                disabled={m.disabled}
                onClick={() => set({ selection_mode: m.key })}
                Icon={m.Icon}
                badge={m.disabled ? t("promo.wizard.selectionMode.comingSoon") : null}
                title={t(`promo.wizard.selectionMode.opt.${m.key}.title`)}
                description={t(`promo.wizard.selectionMode.opt.${m.key}.desc`)}
              />
            ))}
          </div>
        </div>
      );
    }

    case "product": {
      const src = wd.sources.find((s) => s.id === form.connection_id);
      const mkt = MARKETPLACES.find((m) => m.key === form.source_platform);
      const list = props.products.list;
      const manual = form.selection_mode === "manual";
      const selectedCount = form.selected_product_ids.length;
      const maxN = Number(form.max_items) || 0;
      const atLimit = selectedCount >= maxN;
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.product.title"), manual ? t("promo.wizard.product.manualHelp") : t("promo.wizard.product.help"))}

          {/* Source info panel */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-border bg-card px-4 py-3" data-testid="product-source-panel">
            <div className="flex items-center gap-2">
              {mkt ? <mkt.Icon size={18} /> : <Storefront size={18} />}
              <div className="leading-tight">
                <div className="text-sm font-medium">{src?.name || mkt?.label || "—"}</div>
                <div className="promo-overline">{mkt?.label || form.source_platform}</div>
              </div>
            </div>
            <PanelStat label={t("promo.wizard.product.available")} value={props.products.loading ? "…" : list.length} />
            {(props.syncState?.syncing || props.syncState?.at || src?.last_sync_at) && (
              <PanelStat label={t("promo.wizard.product.lastSync")}
                value={props.syncState?.syncing ? t("promo.wizard.product.syncing") : fmtRelative(props.syncState?.at || src?.last_sync_at)} />
            )}
            <div className="flex items-center gap-2">
              <StatusDot status={src?.status === "connected" ? "connected" : (src?.status || "disconnected")} />
              <span className="text-xs text-muted-foreground">{t(src?.status === "connected" ? "promo.conn.connected" : "promo.conn.disconnected")}</span>
            </div>
          </div>

          <div className="max-w-xs space-y-2">
            <Label>{t("promo.wizard.product.max")}</Label>
            <Input type="number" min={1} max={100} value={form.max_items}
              onChange={(e) => props.onMaxChange(e.target.value)} className="bg-card" data-testid="wizard-max-items" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{manual ? t("promo.wizard.product.pickTitle") : t("promo.wizard.product.catalog")}</Label>
              {manual ? (
                <span className={cn("text-xs font-medium tabular-nums", atLimit ? "text-brand" : "text-muted-foreground")} data-testid="selection-counter">
                  {t("promo.wizard.product.selectedCount", { n: selectedCount, max: maxN })}
                </span>
              ) : (
                <span className="text-xs tabular-nums text-muted-foreground" data-testid="catalog-count">
                  {props.products.loading ? t("common.loading") : t("promo.wizard.product.count", { n: list.length })}
                </span>
              )}
            </div>
            {!props.products.loading && list.length === 0 ? (
              <EmptyState Icon={Package} title={t("promo.wizard.product.emptyTitle")} description={t("promo.wizard.product.emptyDesc")} />
            ) : manual ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="manual-product-grid">
                {list.map((p) => {
                  const id = String(p.external_id || p.id);
                  const isSel = form.selected_product_ids.includes(id);
                  return (
                    <ProductCard
                      key={id} product={p} testId={`select-item-${id}`}
                      selectable selected={isSel} disabled={!isSel && atLimit}
                      onToggle={() => props.toggleProduct(id)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {list.slice(0, 12).map((p) => (
                  <ProductCard key={p.external_id || p.id} product={p} testId={`catalog-item-${p.external_id || p.id}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    case "strategy":
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.strategy.title"), t("promo.wizard.strategy.help"))}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(wd.strategies.length ? wd.strategies : Object.keys(STRATEGY_META)).map((s) => (
              <SelectableCard
                key={s} testId={`strategy-${s}`} selected={form.selection_strategy === s}
                onClick={() => set({ selection_strategy: s })}
                Icon={STRATEGY_META[s]?.Icon} badge={STRATEGY_META[s]?.ai ? "AI" : null}
                title={t(`promo.wizard.strategy.opt.${s}.title`)} description={t(`promo.wizard.strategy.opt.${s}.desc`)}
              />
            ))}
          </div>
        </div>
      );

    case "mode":
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.mode.title"), t("promo.wizard.mode.help"))}
          <div className="grid grid-cols-1 gap-3">
            {(wd.modes.length ? wd.modes : Object.keys(MODE_META)).map((m) => (
              <SelectableCard
                key={m} testId={`mode-${m}`} selected={form.generation_mode === m}
                onClick={() => set({ generation_mode: m })}
                Icon={MODE_META[m]?.Icon} badge={MODE_META[m]?.ai ? "AI" : null}
                title={t(`promo.wizard.mode.opt.${m}.title`)} description={t(`promo.wizard.mode.opt.${m}.desc`)}
              />
            ))}
          </div>
          {form.generation_mode === "original" && (
            <div className="mt-4 rounded-lg border border-dashed border-input bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground"
                 data-testid="original-mode-note">
              {t("promo.wizard.mode.originalNote")}
            </div>
          )}
        </div>
      );

    case "platforms":
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.platforms.title"), t("promo.wizard.platforms.help"))}
          {wd.accounts.length === 0 ? (
            <EmptyState Icon={ShareNetwork} title={t("promo.wizard.platforms.empty")} description={t("promo.wizard.platforms.emptyDesc")} />
          ) : (
            SOCIAL_PLATFORMS.filter((p) => props.accountsByPlatform[p.key]?.length).map((p) => (
              <div key={p.key} className="space-y-2">
                <div className="flex items-center gap-2 promo-overline"><p.Icon size={13} /> {p.label}</div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {props.accountsByPlatform[p.key].map((a) => (
                    <SelectableCard
                      key={a.id} testId={`account-${a.id}`} selected={form.account_ids.includes(a.id)}
                      onClick={() => props.toggleAccount(a)} Icon={p.Icon} title={a.handle || a.id}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      );

    case "interval":
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.interval.title"), t("promo.wizard.interval.help"))}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("promo.wizard.interval.every")}</Label>
              <Input type="number" min={1} value={form.interval_value}
                onChange={(e) => set({ interval_value: e.target.value })} className="bg-card" data-testid="wizard-interval-value" />
            </div>
            <div className="space-y-2">
              <Label>{t("promo.wizard.interval.unit")}</Label>
              <PromoSelect value={form.interval_unit} onChange={(v) => set({ interval_unit: v })}
                testId="wizard-interval-unit"
                options={UNITS.map((u) => ({ value: u, label: t(`promo.wizard.interval.units.${u}`) }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("promo.wizard.interval.startAt")}</Label>
              <Input type="datetime-local" value={form.start_at}
                onChange={(e) => set({ start_at: e.target.value })} className="bg-card" data-testid="wizard-start-at" />
            </div>
            <div className="space-y-2">
              <Label>{t("promo.wizard.interval.timezone")}</Label>
              <Input value={form.timezone} onChange={(e) => set({ timezone: e.target.value })} className="bg-card" data-testid="wizard-timezone" />
            </div>
          </div>
        </div>
      );

    case "language":
      return (
        <div className="max-w-sm space-y-6">
          {heading(t("promo.wizard.language.title"), t("promo.wizard.language.help"))}
          <PromoSelect value={form.language} onChange={(v) => set({ language: v })} options={LANGUAGES} testId="wizard-language" />
        </div>
      );

    case "kb":
      return (
        <div className="max-w-sm space-y-6">
          {heading(t("promo.wizard.kb.title"), t("promo.wizard.kb.help"))}
          <PromoSelect value={form.kb_id} onChange={(v) => set({ kb_id: v })} testId="wizard-kb"
            disabled={form.generation_mode === "original"}
            placeholder={form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : t("promo.wizard.optNone")}
            options={[{ value: null, label: t("promo.wizard.optNone") }, ...wd.kbs.map((k) => ({ value: k.id, label: k.name }))]} />
        </div>
      );

    case "prompt":
      return (
        <div className="max-w-md space-y-6">
          {heading(t("promo.wizard.prompt.title"), t("promo.wizard.prompt.help"))}
          <PromoSelect value={form.prompt_template} onChange={(v) => set({ prompt_template: v })} testId="wizard-prompt"
            disabled={form.generation_mode === "original"}
            placeholder={form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : t("promo.wizard.optAuto")}
            options={[{ value: null, label: t("promo.wizard.optAuto") }, ...wd.prompts.map((p) => ({ value: p.key, label: p.key }))]} />
        </div>
      );

    case "provider":
      return (
        <div className="max-w-md space-y-6">
          {heading(t("promo.wizard.provider.title"), t("promo.wizard.provider.help"))}
          <PromoSelect value={form.provider} onChange={(v) => set({ provider: v })} testId="wizard-provider"
            disabled={form.generation_mode === "original"}
            placeholder={form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : t("promo.wizard.optAuto")}
            options={[{ value: null, label: t("promo.wizard.optAuto") },
              ...wd.providers.map((p) => ({ value: p.provider, label: `${p.provider} · ${p.model}` }))]} />
        </div>
      );

    case "preview": {
      const { result, loading } = props.preview;
      return (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            {heading(t("promo.wizard.preview.title"))}
            <button onClick={props.runPreview} disabled={loading}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm transition-colors hover:bg-accent disabled:opacity-50"
              data-testid="wizard-regenerate">
              <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> {t("promo.wizard.preview.regen")}
            </button>
          </div>
          {loading ? (
            <div className="promo-beam rounded-lg border border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="preview-loading">
              {t("promo.wizard.preview.generating")}
            </div>
          ) : !result || !result.product ? (
            <EmptyState Icon={Eye} title={t("promo.wizard.preview.emptyTitle")} description={t("promo.wizard.preview.emptyDesc")} />
          ) : (
            <div className="space-y-4" data-testid="preview-results">
              <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm">
                <span className="text-muted-foreground">{t("promo.wizard.preview.product")}: </span>
                <span className="font-medium">{result.product.title}</span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {result.previews.map((pv, i) => <SocialPreviewCard key={pv.platform} preview={pv} index={i} />)}
              </div>
            </div>
          )}
        </div>
      );
    }

    case "review": {
      const rows = [
        ["source", form.name],
        ["marketplace", MARKETPLACES.find((m) => m.key === form.source_platform)?.label || "—"],
        ["selectionMode", t(`promo.wizard.selectionMode.opt.${form.selection_mode}.title`)],
        ["strategy", t(`promo.wizard.strategy.opt.${form.selection_strategy}.title`)],
        ["mode", t(`promo.wizard.mode.opt.${form.generation_mode}.title`)],
        ["platforms", form.platforms.map((p) => PLATFORM_BY_KEY[p]?.label || p).join(", ") || "—"],
        ["accounts", String(form.account_ids.length)],
        ["interval", `${form.interval_value} ${t(`promo.wizard.interval.units.${form.interval_unit}`)}`],
        ["max_items", String(form.max_items)],
        ["language", LANGUAGES.find((l) => l.value === form.language)?.label],
        ["kb", form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : (wd.kbs.find((k) => k.id === form.kb_id)?.name || t("promo.wizard.optNone"))],
        ["prompt", form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : (form.prompt_template || t("promo.wizard.optAuto"))],
        ["provider", form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : (form.provider || t("promo.wizard.optAuto"))],
      ];
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.review.title"), t("promo.wizard.review.help"))}

          {/* Campaign Executive Summary */}
          <ExecutiveSummary form={form} wd={wd} t={t} />

          {/* PLATFORM-SPECIFIC publishing options (capability-driven). Only the sections for
              the selected platforms are shown, and only with options each publisher supports.
              Never affects the generated caption — only how the product is presented. */}
          {(() => {
            const sel = new Set(form.platforms);
            const hasFacebook = sel.has("facebook");
            const hasInstagram = sel.has("instagram");
            const hasLinkedIn = sel.has("linkedin");
            const hasFbProfile = wd.accounts.some(
              (a) => form.account_ids.includes(a.id) && a.connector_id === "social.meta_facebook_web");
            const fbWebAccountId = (wd.accounts.find(
              (a) => form.account_ids.includes(a.id) && a.connector_id === "social.meta_facebook_web") || {}).id || null;
            // Instagram Story is gated on a CONNECTED + HEALTHY Browser Publisher session among
            // the selected Instagram accounts (SPRINT-118). No valid session → toggle hidden.
            const hasIgWebSession = wd.accounts.some(
              (a) => form.account_ids.includes(a.id) && a.connector_id === "social.meta_instagram_web"
                && a.session_present && a.session_health === "healthy");
            if (!hasFacebook && !hasInstagram && !hasLinkedIn) return null;
            return (
              <div className="space-y-4" data-testid="wizard-publishing-options">
                <div>
                  <h3 className="text-base font-medium tracking-tight">{t("promo.wizard.publishing.title")}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("promo.wizard.publishing.help")}</p>
                </div>

                {hasFacebook && (
                  <div className="rounded-lg border border-border bg-card p-5" data-testid="publishing-facebook">
                    <div className="promo-overline mb-3">{t("promo.wizard.publishing.fbTitle")}</div>
                    <DisplayStylePicker value={form.platform_options.facebook.display_style}
                      onChange={(v) => setPO("facebook", { display_style: v })} t={t}
                      testidPrefix="fb-display-style" />
                    {hasFbProfile && (
                      <label className="mt-4 flex items-center justify-between rounded-lg border border-border p-4">
                        <div>
                          <div className="text-sm font-medium">{t("promo.wizard.publishing.storyLabel")}</div>
                          <p className="mt-0.5 text-xs text-muted-foreground">{t("promo.wizard.publishing.storyNote")}</p>
                        </div>
                        <Switch checked={form.platform_options.facebook.publish_to_story}
                          onCheckedChange={(v) => setPO("facebook", { publish_to_story: v })}
                          data-testid="fb-publish-story" />
                      </label>
                    )}
                    {hasFbProfile && (
                      <FacebookGroupsSection fbAccountId={fbWebAccountId}
                        value={form.platform_options.facebook_groups}
                        onChange={(patch) => setPO("facebook_groups", patch)} t={t} />
                    )}
                  </div>
                )}

                {hasLinkedIn && (
                  <div className="rounded-lg border border-border bg-card p-5" data-testid="publishing-linkedin">
                    <div className="promo-overline mb-3">{t("promo.wizard.publishing.liTitle")}</div>
                    <DisplayStylePicker value={form.platform_options.linkedin.display_style}
                      onChange={(v) => setPO("linkedin", { display_style: v })} t={t}
                      testidPrefix="li-display-style" />
                  </div>
                )}

                {hasInstagram && (
                  <div className="rounded-lg border border-border bg-card p-5" data-testid="publishing-instagram">
                    <div className="promo-overline mb-3">{t("promo.wizard.publishing.igTitle")}</div>
                    <InstagramDisplayPicker value={form.platform_options.instagram.display_style}
                      onChange={(v) => setPO("instagram", { display_style: v })} t={t} />
                    {hasIgWebSession && (
                      <label className="mt-4 flex items-center justify-between rounded-lg border border-border p-4">
                        <div>
                          <div className="text-sm font-medium">{t("promo.wizard.publishing.igStoryLabel")}</div>
                          <p className="mt-0.5 text-xs text-muted-foreground">{t("promo.wizard.publishing.igStoryNote")}</p>
                        </div>
                        <Switch checked={form.platform_options.instagram.publish_to_story}
                          onCheckedChange={(v) => setPO("instagram", { publish_to_story: v })}
                          data-testid="ig-publish-story" />
                      </label>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="overflow-hidden rounded-lg border border-border bg-card" data-testid="review-summary">
            {rows.map(([k, v], i) => (
              <div key={k} className={cn("flex items-center justify-between gap-4 px-4 py-3 text-sm", i > 0 && "border-t border-border/60")}>
                <span className="promo-overline">{t(`promo.wizard.review.f.${k}`)}</span>
                <span className="truncate text-right font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "start":
      return (
        <div className="space-y-6">
          {heading(t("promo.wizard.start.title"), t("promo.wizard.start.help"))}
          <label className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
            <div>
              <div className="text-sm font-medium">{t("promo.wizard.start.autoCampaign")}</div>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("promo.wizard.start.autoCampaignHelp")}</p>
            </div>
            <Switch checked={form.auto_create_campaign} onCheckedChange={(v) => set({ auto_create_campaign: v })} data-testid="wizard-auto-campaign" />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button onClick={() => props.submit("draft")} disabled={props.submitting}
              className="flex items-center justify-center gap-2 rounded-md border border-border px-4 py-3 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
              data-testid="wizard-save-draft">
              <FloppyDisk size={16} /> {t("promo.wizard.start.saveDraft")}
            </button>
            <button onClick={() => props.submit("activate")} disabled={props.submitting}
              className="flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              data-testid="wizard-activate">
              <Rocket size={16} weight="fill" /> {t("promo.wizard.start.activate")}
            </button>
          </div>
          {props.submitting && <p className="text-center text-sm text-muted-foreground">{t("common.loading")}</p>}
        </div>
      );

    default:
      return null;
  }
}


// Managerial one-glance summary for the Review step (presentation-only calculations).
function ExecutiveSummary({ form, wd, t }) {
  const mkt = MARKETPLACES.find((m) => m.key === form.source_platform);
  const manual = form.selection_mode === "manual";
  const products = manual ? form.selected_product_ids.length : (Number(form.max_items) || 0);
  const platformsCount = form.platforms.length || 0;
  const estimatedPosts = products * Math.max(1, platformsCount);
  const durationMs = products > 1 ? intervalMs(form.interval_value, form.interval_unit) * (products - 1) : 0;
  const endDate = durationMs ? new Date(Date.now() + durationMs).toISOString() : null;

  const fields = [
    ["marketplace", mkt?.label || form.source_platform || "—"],
    ["productsSelected", products],
    ["estimatedPosts", estimatedPosts],
    ["platforms", form.platforms.map((p) => PLATFORM_BY_KEY[p]?.label || p).join(", ") || "—"],
    ["interval", `${form.interval_value} ${t(`promo.wizard.interval.units.${form.interval_unit}`)}`],
    ["duration", products > 1 ? `${fmtDuration(durationMs)}${endDate ? ` · ${fmtDateTime(endDate)}` : ""}` : t("promo.wizard.exec.instant")],
    ["aiMode", t(`promo.wizard.mode.opt.${form.generation_mode}.title`)],
    ["provider", form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : (form.provider || t("promo.wizard.optAuto"))],
    ["prompt", form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : (form.prompt_template || t("promo.wizard.optAuto"))],
    ["kb", form.generation_mode === "original" ? t("promo.wizard.originalDisabled") : (wd.kbs.find((k) => k.id === form.kb_id)?.name || t("promo.wizard.optNone"))],
    ["language", form.language?.toUpperCase()],
    ["timezone", form.timezone],
    ["type", form.auto_create_campaign ? t("promo.wizard.exec.socialCampaign") : t("promo.wizard.exec.standalone")],
  ];

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="executive-summary">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-md border border-brand/30 bg-brand/10 text-brand">
          <Sparkle size={16} weight="duotone" />
        </div>
        <div>
          <h3 className="text-base font-medium tracking-tight">{t("promo.wizard.exec.title")}</h3>
          <p className="promo-overline mt-0.5">{form.name || "—"}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
        {fields.map(([k, v]) => (
          <div key={k} data-testid={`exec-${k}`}>
            <div className="promo-overline">{t(`promo.wizard.exec.f.${k}`)}</div>
            <div className="mt-1 truncate text-sm font-medium tabular-nums" title={String(v)}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
